import { getStorage, removeStorage, setStorage } from '@/shared/utils/local-storage';
import { useCallback, useEffect, useMemo, useState } from 'react';

// ----------------------------------------------------------------------

/**
 * Custom hook to manage state with local storage.
 *
 * @example Const { state, resetState, setState, setField } = useLocalStorageV2('settings', initialState);
 *
 * Return ( <div> <p>State: {JSON.stringify(state)}</p> <button onClick={() => setState({name: 'John', age: 20})}>Set
 * State</button> <button onClick={() => setField('name', 'John')}>Set Name</button> <button
 * onClick={resetState}>Reset</button> </div> );
 *
 * @param {string} key - The key for the local storage.
 * @param {T} initialState - The initial state value.
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.initializeWithValue=true] - Whether to initialize the local storage with the initial state
 *   value. Default is `true`
 * @returns {UseLocalStorageV2Return<T>} - An object containing:
 *
 *   - `state`: The current state.
 *   - `resetState`: A function to reset the state to the initial value and remove it from local storage.
 *   - `setState`: A function to update the state and save it to local storage.
 *   - `setField`: A function to update a specific field in the state and save it to local storage.
 */

export type UseLocalStorageV2Options = {
  initializeWithValue?: boolean;
};

export type UseLocalStorageV2Return<T> = {
  state: T;
  resetState: (defaultState?: T) => void;
  setState: (updateState: T | Partial<T>) => void;
  setField: (name: keyof T, updateValue: T[keyof T]) => void;
};

export function useLocalStorageV2<T>(
  key: string,
  initialState?: T,
  options?: UseLocalStorageV2Options
): UseLocalStorageV2Return<T> {
  const { initializeWithValue = true } = options ?? {};
  const isObjectState = initialState && typeof initialState === 'object';

  const [state, setState] = useState<T | undefined>(initialState);

  useEffect(() => {
    const storedValue = getStorage<T>(key);

    if (storedValue) {
      if (isObjectState) {
        setState((prevState) => ({ ...prevState, ...storedValue }));
      } else {
        setState(storedValue);
      }
    } else if (initialState && initializeWithValue) {
      setStorage(key, initialState);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateState = useCallback(
    (newState: T | Partial<T>) => {
      if (isObjectState) {
        setState((prevValue) => {
          const updatedState = { ...prevValue, ...newState } as T;
          setStorage<T>(key, updatedState);
          return updatedState;
        });
      } else {
        setStorage<T>(key, newState as T);
        setState(newState as T);
      }
    },
    [key, isObjectState]
  );

  const updateField = useCallback(
    (fieldName: keyof T, updateValue: T[keyof T]) => {
      if (isObjectState) {
        updateState({ [fieldName]: updateValue } as Partial<T>);
      }
    },
    [isObjectState, updateState]
  );

  const resetState = useCallback(
    (defaultState?: T) => {
      setState(defaultState ?? initialState);
      removeStorage(key);
    },
    [initialState, key]
  );

  const memoizedValue = useMemo(
    () => ({
      state: state as T,
      setState: updateState,
      setField: updateField,
      resetState
    }),
    [resetState, updateField, updateState, state]
  );

  return memoizedValue;
}
